import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Linkedin, Twitter, Youtube, ArrowRight } from 'lucide-react';
import { companyInfo } from '../../data/siteData';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Projects', path: '/projects' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const projectLinks = [
    { name: 'Ongoing Projects', path: '/projects?status=Ongoing' },
    { name: 'Completed Projects', path: '/projects?status=Completed' },
    { name: 'Upcoming Projects', path: '/upcoming' },
    { name: 'Commercial', path: '/projects?category=Commercial' },
    { name: 'Residential', path: '/projects?category=Residential' },
  ];

  const legalLinks = [
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Blog', path: '/blog' },
    { name: 'Admin Portal', path: '/admin/login' },
  ];

  const socialIcons = [
    { Icon: Facebook, link: companyInfo.socialLinks.facebook, name: 'Facebook' },
    { Icon: Instagram, link: companyInfo.socialLinks.instagram, name: 'Instagram' },
    { Icon: Linkedin, link: companyInfo.socialLinks.linkedin, name: 'LinkedIn' },
    { Icon: Twitter, link: companyInfo.socialLinks.twitter, name: 'Twitter' },
    { Icon: Youtube, link: companyInfo.socialLinks.youtube, name: 'YouTube' },
  ];

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Modern Arch Logo" className="h-16 w-auto object-contain bg-white rounded-lg p-2" />
            </div>
            <p className="text-slate-400 font-manrope leading-relaxed">
              Building dreams and creating landmarks since 1998. Your trusted partner for premium residential and commercial developments in Nagpur.
            </p>
            <div className="flex gap-3">
              {socialIcons.map(({ Icon, link, name }) => (
                <a
                  key={name}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${name.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-[#47ace2] transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold font-outfit mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-slate-400 hover:text-[#47ace2] transition-colors font-manrope flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Projects */}
          <div>
            <h4 className="text-lg font-semibold font-outfit mb-6">Projects</h4>
            <ul className="space-y-3">
              {projectLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-[#47ace2] transition-colors font-manrope flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold font-outfit mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin className="w-5 h-5 text-[#47ace2] flex-shrink-0 mt-1" />
                <span className="text-slate-400 font-manrope">{companyInfo.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="w-5 h-5 text-[#47ace2] flex-shrink-0 mt-1" />
                <div className="flex flex-col gap-1">
                  <a href={`tel:${companyInfo.primaryPhone || '+918766551928'}`} className="text-slate-400 hover:text-[#47ace2] transition-colors font-manrope">
                    +91 8766 551 928 (Primary)
                  </a>
                  <a href={`tel:${companyInfo.secondaryPhone || '07124102028'}`} className="text-slate-400 hover:text-[#47ace2] transition-colors font-manrope text-sm">
                    0712-4102028 (Landline)
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <Mail className="w-5 h-5 text-[#47ace2] flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-slate-400 hover:text-[#47ace2] transition-colors font-manrope">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex gap-3">
                <Clock className="w-5 h-5 text-[#47ace2] flex-shrink-0" />
                <span className="text-slate-400 font-manrope">{companyInfo.workingHours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        {/* <div className="mt-16 pt-12 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-lg font-semibold font-outfit mb-2">Subscribe to Our Newsletter</h4>
              <p className="text-slate-400 font-manrope">Get the latest updates on new projects and offers</p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                data-testid="newsletter-email"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 w-full md:w-64"
              />
              <Button
                data-testid="newsletter-submit"
                className="bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-lg px-6"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div> */}
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm font-manrope">
              © {currentYear} {companyInfo.fullName}. All rights reserved.
            </p>
            <div className="flex gap-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-slate-400 hover:text-[#47ace2] text-sm font-manrope transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
